/**
 * 👥 useAffiliateLeads Hook
 * Provides lead data with filtering, sorting, and pagination
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  MOCK_LEADS,
  MOCK_LEAD_STATS,
  getLeadStatusConfig,
  getLeadSourceLabel,
  type Lead,
  type LeadStatus,
  type LeadSource,
} from '@/lib/affiliate-mock-data';

// ============================================
// TYPES
// ============================================

interface LeadStats {
  total: number;
  openLeads: number;
  wonThisMonth: number;
  conversionRate: number;
}

interface LeadFilters {
  search: string;
  status: LeadStatus | 'all';
  source: LeadSource | 'all';
}

interface LeadSorting {
  field: keyof Lead | null;
  direction: 'asc' | 'desc';
}

interface UseLeadsReturn {
  leads: Lead[];
  stats: LeadStats;
  isLoading: boolean;
  error: Error | null;
  filters: LeadFilters;
  sorting: LeadSorting;
  pagination: {
    page: number;
    pageSize: number;
    totalPages: number;
    totalItems: number;
  };
  setSearch: (search: string) => void;
  setStatusFilter: (status: LeadStatus | 'all') => void;
  setSourceFilter: (source: LeadSource | 'all') => void;
  setSorting: (field: keyof Lead | null, direction?: 'asc' | 'desc') => void;
  setPage: (page: number) => void;
  refetch: () => void;
  getStatusBadge: typeof getLeadStatusConfig;
  getSourceLabel: typeof getLeadSourceLabel;
}

// ============================================
// HOOK IMPLEMENTATION
// ============================================

export function useAffiliateLeads(pageSize = 10): UseLeadsReturn {
  const [allLeads, setAllLeads] = useState<Lead[]>([]);
  const [stats, setStats] = useState<LeadStats>(MOCK_LEAD_STATS);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  
  const [filters, setFilters] = useState<LeadFilters>({
    search: '',
    status: 'all',
    source: 'all',
  });
  
  const [sorting, setSortingState] = useState<LeadSorting>({
    field: null,
    direction: 'desc',
  });
  
  const [page, setPage] = useState(1);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      await new Promise(resolve => setTimeout(resolve, 200));
      setAllLeads(MOCK_LEADS);
      setStats(MOCK_LEAD_STATS);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch leads'));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Filter and sort leads
  const filteredLeads = useMemo(() => {
    let result = [...allLeads];
    
    // Apply search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      result = result.filter(lead =>
        lead.restaurant.toLowerCase().includes(searchLower) ||
        lead.contact.toLowerCase().includes(searchLower) ||
        lead.email.toLowerCase().includes(searchLower)
      );
    }
    
    // Apply status filter
    if (filters.status !== 'all') {
      result = result.filter(lead => lead.status === filters.status);
    }
    
    // Apply source filter
    if (filters.source !== 'all') {
      result = result.filter(lead => lead.source === filters.source);
    }
    
    // Apply sorting
    if (sorting.field) {
      result.sort((a, b) => {
        const aVal = a[sorting.field!];
        const bVal = b[sorting.field!];
        const comparison = String(aVal).localeCompare(String(bVal));
        return sorting.direction === 'asc' ? comparison : -comparison;
      });
    }
    
    return result;
  }, [allLeads, filters, sorting]);

  // Paginate
  const paginatedLeads = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredLeads.slice(start, start + pageSize);
  }, [filteredLeads, page, pageSize]);

  const totalPages = Math.ceil(filteredLeads.length / pageSize);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [filters]);

  return {
    leads: paginatedLeads,
    stats,
    isLoading,
    error,
    filters,
    sorting,
    pagination: {
      page,
      pageSize,
      totalPages,
      totalItems: filteredLeads.length,
    },
    setSearch: (search: string) => setFilters(f => ({ ...f, search })),
    setStatusFilter: (status: LeadStatus | 'all') => setFilters(f => ({ ...f, status })),
    setSourceFilter: (source: LeadSource | 'all') => setFilters(f => ({ ...f, source })),
    setSorting: (field, direction = 'desc') => setSortingState({ field, direction }),
    setPage,
    refetch: fetchData,
    getStatusBadge: getLeadStatusConfig,
    getSourceLabel: getLeadSourceLabel,
  };
}

export default useAffiliateLeads;
